import { GoogleAnalyticsIds } from "constants/google-analytics-ids";
import { useAtomValue } from "jotai";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal, { CloseDialogEvent, ModalProps } from "molecules/modals/modal";
import PageNavigationMenu from "molecules/page-navigation/page-navigation-menu";
import SearchModalFiltersContainer from "organisms/modals/search-modal/filters/search-modal-filters-container";
import SearchModalHeader from "organisms/modals/search-modal/search-modal-header";
import SearchModalResults from "organisms/modals/search-modal/search-modal-results";
import {
    SearchModalResultType,
    SearchStatus,
} from "organisms/modals/search-modal/utils/search-modal-reducer";
import useSearchModal from "organisms/modals/search-modal/utils/use-search-modal";
import React, { useEffect } from "react";
import CurrentPublicationEntityAtom from "utilities/atoms/current-publication-entity-atom";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import { AriaRole } from "utilities/enumerations/aria-role";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { useLocalization } from "utilities/hooks/use-localization";
import useModalActions from "utilities/hooks/use-modal-actions";
import usePagedResults from "utilities/hooks/use-paged-results";
import { useSearchEvents } from "utilities/contexts/search-events/search-events-provider";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import SearchResultsSortOrderOptions from "models/enumerations/sorting/search-results/search-sort-order-by";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import SearchSortOrderBy from "models/enumerations/sorting/search-results/search-sort-order-by";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface SearchModalProps
    extends Pick<ModalProps, "closeDialog" | "isVisible" | "onCloseComplete"> {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS_NAME = "c-search-modal-v2";
const DEFAULT_ONLINE_PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_OFFLINE_PAGE_SIZES = [10, 20];
const DEFAULT_TAKE_SIZE = 10;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Enums
// -----------------------------------------------------------------------------------------

// #endregion Enums

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const SearchModal: React.FunctionComponent<SearchModalProps> = (props) => {
    const { useSearchResultsOrderSelection } = useFeatureFlags();
    const { globalState } = useGlobalState();
    const { closeDialog, isVisible, onCloseComplete } = props;
    const searchResultsDisplayOrderSetting =
        globalState.getUserSettingValue<SearchResultsSortOrderOptions>(
            "SearchResultsSortBy"
        );
    const { continueSession, endSession } = useSearchEvents();
    const { t } = useLocalization();
    const { isOnline } = useNetworkInformation();
    const {
        isOpen: filterModalIsOpen,
        handleOpen: handleOpenFilterModal,
        handleClose: handleCloseFilterModal,
    } = useModalActions();
    const defaultPageSizes = isOnline
        ? DEFAULT_ONLINE_PAGE_SIZES
        : DEFAULT_OFFLINE_PAGE_SIZES;

    const currentPublicationEntity = useAtomValue(CurrentPublicationEntityAtom);
    const { publication } = useCurrentPublication();

    const safeSortOrder = useSearchResultsOrderSelection
        ? searchResultsDisplayOrderSetting
        : SearchSortOrderBy.Relevance;

    const [sortOrder, setSortOrder] = React.useState<number>(
        safeSortOrder ?? 0
    );

    const {
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onPageSizeChange,
        onSelectPage,
        resetPagination,
        setRowCount,
        skip,
        take,
    } = usePagedResults(DEFAULT_TAKE_SIZE);

    const {
        codesEntityTypes,
        codesRowCount,
        codesSearchResults,
        directEntityTypes,
        directRowCount,
        directSearching,
        directSearchResults,
        enhancedContentTypes,
        favoritePublications,
        handleClearFilters,
        handleCodesEntityTypesUpdated,
        handleDirectEntityTypesUpdated,
        handleEnhancedContentTypesUpdated,
        handleSearchClear,
        handleSelectedPublicationsUpdated,
        handleSetReducerState,
        handleSetSelectedResultType,
        handleSortOrder,
        handleSubmit,
        publications,
        publicationSearching,
        searchText,
        selectedPublicationIds,
        selectedResultType,
        selectedRowCount,
        status,
    } = useSearchModal({
        currentPublicationEntity,
        currentPublication: publication,
        skip,
        take,
        resetPagination,
        setPagingRowCount: setRowCount,
        sortOrder,
    });

    const handleClose = (event?: CloseDialogEvent) => closeDialog(event);

    const transition =
        window.innerWidth < Breakpoints.Phone
            ? ModalTransitions.SlideUp
            : ModalTransitions.SlideRight;
    const offlineClassName = !isOnline ? "-offline" : "";
    const results =
        selectedResultType === SearchModalResultType.Codes
            ? codesSearchResults
            : directSearchResults;

    const entityTypes =
        selectedResultType === SearchModalResultType.Codes
            ? [...codesEntityTypes, ...enhancedContentTypes]
            : directEntityTypes;

    const searchHintText = t("inputHintText", {
        text: t("searchHintText"),
    });

    const handleSetSortOrder = (value: number) => {
        handleSortOrder(value);
        setSortOrder(value);
    };

    // Track previous publication ID to detect when it changes
    const prevPublicationIdRef = React.useRef<number | undefined>(
        publication?.id
    );

    const prevSortOrderRef = React.useRef<number>(sortOrder);

    useEffect(() => {
        // Update the prev publication ref when modal is visible
        if (isVisible) {
            prevPublicationIdRef.current = publication?.id;
        }

        // Update sort order if it has changed and resubmit the search
        if (prevSortOrderRef.current !== sortOrder) {
            handleSetSortOrder(sortOrder);
            prevSortOrderRef.current = sortOrder;
            // Then re-search if there's search text
            if (searchText !== "") {
                handleSubmit();
            }
        }

        // If the publication has changed while the modal is open
        if (isVisible && prevPublicationIdRef.current !== publication?.id) {
            // Then re-search if there's search text
            if (searchText !== "") {
                handleSubmit();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, sortOrder]);

    const isSearchingAllPublications = selectedPublicationIds.length === 0;

    useEffect(() => {
        // Continue the current search session
        if (isVisible) {
            continueSession();
            // Start the timer to end the search session
        } else {
            endSession();
        }
    }, [continueSession, endSession, isVisible]);

    return (
        <Modal
            closeDialog={handleClose}
            isVisible={isVisible}
            label={t("searchItem", { item: t("menu") })}
            onCloseComplete={onCloseComplete}
            showAppSidebar={true}
            transition={transition}
            type={ModalTypes.Search}>
            <div className={`${BASE_CLASS_NAME} ${offlineClassName}`}>
                <SearchModalHeader
                    directResultsCount={directRowCount}
                    directSearching={directSearching}
                    favoritePublications={favoritePublications}
                    hintText={searchHintText}
                    onSearchClear={handleSearchClear}
                    onSearchTextChange={(text: string) =>
                        handleSetReducerState({ searchText: text })
                    }
                    onSubmit={handleSubmit}
                    publicationResultsCount={codesRowCount}
                    publications={publications}
                    publicationSearching={publicationSearching}
                    searchText={searchText}
                    selectedResultType={selectedResultType}
                    setSelectedResultType={handleSetSelectedResultType}
                />
                <div
                    className={`${BASE_CLASS_NAME}__content`}
                    role={AriaRole.TabPanel}>
                    <SearchModalFiltersContainer
                        codesEntityTypes={codesEntityTypes}
                        directEntityTypes={directEntityTypes}
                        enhancedContentTypes={enhancedContentTypes}
                        favoritePublications={favoritePublications}
                        isModalOpen={filterModalIsOpen}
                        onClearFilters={handleClearFilters}
                        onCodesEntityTypesUpdated={
                            handleCodesEntityTypesUpdated
                        }
                        onDirectEntityTypesUpdated={
                            handleDirectEntityTypesUpdated
                        }
                        onEnhancedContentTypesUpdated={
                            handleEnhancedContentTypesUpdated
                        }
                        onModalClose={handleCloseFilterModal}
                        onSelectedPublicationsUpdated={
                            handleSelectedPublicationsUpdated
                        }
                        publications={publications}
                        resetPagination={resetPagination}
                        searchResultType={selectedResultType}
                        selectedPublications={selectedPublicationIds}
                    />
                    <SearchModalResults
                        filterCount={getFilterCount(
                            entityTypes,
                            selectedPublicationIds
                        )}
                        loading={
                            status !== SearchStatus.Initial &&
                            (publicationSearching || directSearching)
                        }
                        onFilterModalOpen={handleOpenFilterModal}
                        onResultClick={handleClose}
                        results={results}
                        resultTotal={selectedRowCount}
                        searchText={searchText}
                        isSearchingAllPublications={isSearchingAllPublications}
                        setSortOrder={(value: number) =>
                            handleSetSortOrder(value)
                        }
                        sortOrder={sortOrder}
                    />
                </div>
                {selectedRowCount > 0 && (
                    <div className={`${BASE_CLASS_NAME}__footer`}>
                        <PageNavigationMenu
                            currentPage={currentPage}
                            id={GoogleAnalyticsIds.SEARCH_MODAL_PAGE_NAVIGATION}
                            numberOfPages={numberOfPages}
                            onNavigateBack={onPageLast}
                            onNavigateForward={onPageNext}
                            onPageSizeChange={onPageSizeChange}
                            onSelectPage={onSelectPage}
                            pageSize={take}
                            pageSizes={defaultPageSizes}
                            resultTotal={selectedRowCount}
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

const getFilterCount = (
    selectedEntityTypes: Array<string>,
    selectedFavorites: Array<number>
) => selectedEntityTypes.length + selectedFavorites.length;

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

export default SearchModal;

// #endregion Export
